import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { ThemeProvider, createTheme } from "@material-ui/core/styles";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Sidebar, {
  SIDEBAR_WIDTH,
  SIDEBAR_WIDTH_COLLAPSED,
} from "../UserDashboard/Sidebar";
import { useSession } from "./useSession";
import { usePageStyles } from "./pageStyles";
import { getBackTarget } from "./detailBack";
import { COLORS } from "../UserDashboard/dashboardConfig";
import DescriptionIcon from "@mui/icons-material/Description";

// Motyw starszych komponentow (@material-ui v4) dopasowany do kolorow nowego wygladu -
// strony w tym ukladzie, ktore jeszcze uzywaja v4 (np. szczegoly), nie wygladaja jak z innej aplikacji.
const legacyTheme = createTheme({
  palette: {
    primary: { main: COLORS.primary, dark: COLORS.primaryDark },
    secondary: { main: COLORS.danger },
  },
});

const COLLAPSED_KEY = "sidebarCollapsed";

// Pozycje menu, do ktorych przypisujemy podstrony (np. /kontrolaWewnetrzna/szczegoly/5).
const SECTION_ROOTS = [
  "/user",
  "/tranzyt",
  "/archive",
  "/archiveTranzyt",
  "/magazynWarszawaNowyHandling",
  "/magazynWarszawaZwrotyIBledy",
  "/magazynWarszawa",
  "/magazynWarszawaHandling",
  "/magazynWarszawaLists",
  "/raportMagazynWarszawa",
  "/raportCzasowOdpraw",
  "/list",
  "/adminPanel",
  "/rejestrWnioskow",
  "/kontrolaWewnetrzna",
  "/traces",
  "/history",
  "/calculator",
  "/calculatorZrealizowan&Przygotowane",
];

const resolveActivePath = (pathname) =>
  SECTION_ROOTS.filter((root) => pathname === root || pathname.startsWith(`${root}/`)).sort(
    (a, b) => b.length - a.length,
  )[0] || pathname;

const readCollapsed = () => {
  try {
    return localStorage.getItem(COLLAPSED_KEY) === "true";
  } catch (e) {
    return false;
  }
};

/**
 * Trwały szkielet aplikacji: boczne menu + obszar treści. Jest elementem trasy nadrzędnej,
 * więc przy przejściu między stronami menu się nie montuje od nowa - zmienia się tylko jego
 * zawartość i widok po prawej. Na stronie szczegółów (/detail/*) menu zawęża się do
 * "Powrót" i "Wyloguj" (to samo dla edycji wniosku w Rejestrze).
 */
const ShellLayout = () => {
  const classes = usePageStyles();
  const navigate = useNavigate();
  const location = useLocation();
  const session = useSession();
  const [collapsed, setCollapsed] = useState(readCollapsed);

  useEffect(() => {
    try {
      localStorage.setItem(COLLAPSED_KEY, String(collapsed));
    } catch (e) {
      // brak zapisu - menu po prostu nie zapamięta stanu
    }
  }, [collapsed]);

  const handleLogout = useCallback(() => {
    localStorage.clear();
    delete axios.defaults.headers.common["Authorization"];
    delete axios.defaults.headers.common["X-Use-New-Db"];
    toast.success("Wylogowano.", {
      autoClose: 500,
      onClose: () => navigate("/login"),
    });
  }, [navigate]);

  // Ostatnia strona spoza /form - cel powrotu z formularza.
  const prevPathRef = useRef("/user");
  const back = getBackTarget(location.pathname, location.state, prevPathRef.current);
  useEffect(() => {
    if (!location.pathname.startsWith("/form")) {
      prevPathRef.current = location.pathname + location.search;
    }
  }, [location.pathname, location.search]);

  return (
    <ThemeProvider theme={legacyTheme}>
      <Sidebar
        collapsed={collapsed}
        onToggle={() => setCollapsed((v) => !v)}
        username={session.username}
        role={session.role}
        isLists={session.isLists}
        isMagazynLists={session.isMagazynLists}
        isAdmin={session.isAdmin}
        isRaportCzasow={session.isRaportCzasow}
        isRaportMw={session.isRaportMw}
        activePath={resolveActivePath(location.pathname)}
        back={back}
        backExtras={
          location.pathname === "/form"
            ? [{ label: "Szablony", icon: DescriptionIcon, path: "/form/templates" }]
            : []
        }
        onNavigate={(path) => navigate(path)}
        onLogout={handleLogout}
      />
      <main
        className={classes.main}
        style={{ marginLeft: collapsed ? SIDEBAR_WIDTH_COLLAPSED : SIDEBAR_WIDTH }}
      >
        <Outlet />
      </main>
      <ToastContainer position="top-right" />
    </ThemeProvider>
  );
};

export default ShellLayout;
